import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
  Grid,
  CircularProgress,
} from '@mui/material';
import { LocationOn, Logout } from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { InlineMessage, RegionSelect } from '../components/ui';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { regionCountryCode } from '../utils/regionCountry';
import { validatePhone, normalizePhone, validatePostalCode, normalizePostalCode } from '../utils/inputValidators';

interface LocationAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface LocationForm {
  name: string;
  address: LocationAddress;
  phone: string;
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

/**
 * Onboarding step: add the store's first location, pre-filled from the
 * address captured at signup, or explicitly defer it. Pre-shell page (no
 * Layout/sidebar), matching its onboarding siblings — no PageContainer or
 * PageHeader here.
 */
export const OnboardingStoreLocationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [form, setForm] = useState<LocationForm | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['onboarding', 'store-locations'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/store-locations', { headers });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to load your store locations'));
      }
      return response.json();
    },
  });

  const existingLocations = data?.data?.locations || [];
  const seed = data?.data?.seed;

  useEffect(() => {
    if (seed && !form) {
      setForm({
        name: seed.name || '',
        address: {
          street: seed.address?.street || '',
          city: seed.address?.city || '',
          state: seed.address?.state || '',
          zip: seed.address?.zip || '',
          country: seed.address?.country || '',
        },
        phone: seed.phone || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const addMutation = useMutation({
    mutationFn: async (input: LocationForm) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/store-locations', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: input.name,
          address: input.address,
          phone: input.phone || null,
        }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to add this location'));
      }
      return response.json();
    },
    onSuccess: (result) => {
      navigate(result?.data?.nextStep || '/app/dashboard');
    },
  });

  const skipMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/store-locations/skip', {
        method: 'POST',
        headers,
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to skip this step'));
      }
      return response.json();
    },
    onSuccess: (result) => {
      navigate(result?.data?.nextStep || '/app/dashboard');
    },
  });

  const handleFieldChange = (field: 'name' | 'phone', value: string) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleAddressChange = (field: keyof LocationAddress, value: string) => {
    setForm((prev) => (prev ? { ...prev, address: { ...prev.address, [field]: value } } : prev));
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
      setLoggingOut(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pt: { xs: 6, md: 10 },
        pb: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>

        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Add your store's location
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We've pre-filled this from the address you gave us. Confirm it, edit it, or add
            locations later from Store Settings.
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} />
          </Box>
        ) : isError ? (
          <InlineMessage severity="error">
            {error instanceof Error ? error.message : 'Failed to load your store locations'}
          </InlineMessage>
        ) : existingLocations.length > 0 ? (
          // Already has a location (added here or added elsewhere while this
          // page was open in two tabs) — nothing left to confirm.
          <Card>
            <CardContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {existingLocations[0].name} is set up as your primary location.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/app/dashboard')}
                sx={{ fontWeight: 600 }}
              >
                Continue
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              {addMutation.isError && (
                <InlineMessage severity="error">
                  {addMutation.error instanceof Error
                    ? addMutation.error.message
                    : 'Failed to add this location'}
                </InlineMessage>
              )}
              {skipMutation.isError && (
                <InlineMessage severity="error">
                  {skipMutation.error instanceof Error
                    ? skipMutation.error.message
                    : 'Failed to skip this step'}
                </InlineMessage>
              )}

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Location Name"
                  value={form?.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  required
                />

                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Address
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        value={form?.address.street || ''}
                        onChange={(e) => handleAddressChange('street', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="City"
                        value={form?.address.city || ''}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <RegionSelect
                        fullWidth
                        size="medium"
                        country={regionCountryCode(form?.address.country)}
                        label="State / Province"
                        value={form?.address.state || ''}
                        onChange={(v) => handleAddressChange('state', v)}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <ValidatedTextField
                        fullWidth
                        label="ZIP / Postal Code"
                        value={form?.address.zip || ''}
                        onChange={(e) => handleAddressChange('zip', e.target.value)}
                        validate={(v) => validatePostalCode(v, regionCountryCode(form?.address.country))}
                        normalize={(v) => normalizePostalCode(v, regionCountryCode(form?.address.country))}
                        onNormalized={(v) => handleAddressChange('zip', v)}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <ValidatedTextField
                  fullWidth
                  label="Phone"
                  value={form?.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  validate={validatePhone}
                  normalize={normalizePhone}
                  onNormalized={(v) => handleFieldChange('phone', v)}
                />
              </Stack>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                  variant="text"
                  size="large"
                  onClick={() => skipMutation.mutate()}
                  disabled={skipMutation.isPending || addMutation.isPending}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  I'll do this later
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => form && addMutation.mutate(form)}
                  disabled={!form?.name || addMutation.isPending || skipMutation.isPending}
                  sx={{ minWidth: 200, textTransform: 'none', fontWeight: 600 }}
                >
                  {addMutation.isPending ? 'Adding...' : 'Add this location'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default OnboardingStoreLocationsPage;
